import React, { ReactElement, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Button, Input, SalvModal } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormikState } from 'formik/dist/types';
import * as Yup from 'yup';
import './AlertFiltersModal.scss';
import AlertFilterCondition, { AlertFiltersConditionExpressionProps } from './AlertFilterCondition';
import { AlertFilterObjects } from './helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import {
  InternalApiAlertFilter,
  InternalApiAlertFilterExpressionFieldTypeEnum,
  InternalApiAlertFilterExpressionOperationEnum,
  InternalApiAlertFilterExpressionTargetEnum,
  InternalApiRole,
} from '../../api-internal';
import { addNewAlertFilter, deleteAlertFilter, updateAlertFilter } from './alertFiltersActions';
import dayjs from 'dayjs';
import { AlertFilterRolesSelector } from './AlertFilterRolesSelector';
import { useNavigate } from 'react-router';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  expressions: Yup.array()
    .of(
      Yup.object().shape({
        target: Yup.object().shape({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
          label: Yup.string().required('Required'),
        }),
        field: Yup.object().shape({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
          label: Yup.string().required('Required'),
        }),
        fieldType: Yup.string().required('Required'),
        operation: Yup.object().shape({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
          label: Yup.string().required('Required'),
        }),
        value: Yup.mixed().nullable(),
      })
    )
    .required('Required'),
});

const isDropdownOption = (value: DropdownOption | undefined): value is DropdownOption => !!value;

export const findSelectedOptionValues = (
  values?: Array<unknown>,
  options?: Array<DropdownOption>
): Array<DropdownOption> => {
  if (!options || !values) {
    return [];
  }
  return values.map((value: unknown) => options.find((option) => option.key === value)).filter(isDropdownOption);
};

export interface AlertFiltersModalFormValues {
  id?: number;
  name: string;
  expressions: AlertFiltersConditionExpressionProps[];
  roles: DropdownOption[];
}

export const AlertFiltersModal = ({ selectedFilter, alertFiltersObjects }: Props): ReactElement | null => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);

  const toInternalApiRole = (role: DropdownOption): InternalApiRole => {
    return {
      id: Number(role.key),
    };
  };

  const initialValues: {
    id?: number;
    name: string;
    expressions: AlertFiltersConditionExpressionProps[];
    roles: DropdownOption[];
  } = {
    id: selectedFilter?.id,
    name: '',
    expressions: [],
    roles: [],
  };

  const initialFormikState: Partial<FormikState<AlertFiltersModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const deleteFilter = async (id: number | undefined, setSubmitting: any, resetForm: any) => {
    try {
      if (!id) {
        return;
      }
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'This filter will be deleted.',
          confirmationText: 'Yes, delete it!',
          cancelText: 'No, keep it',
          onConfirm: () => {
            dispatch(deleteAlertFilter(id));
            closeModal(resetForm);
            navigate('/alert-filters');
          },
        })
      );
    } catch (_) {
      dispatch(
        showFeedback({
          message: `Deleting filter ${id} failed`,
          success: false,
        })
      );
    }
  };

  const submit = async (
    values: AlertFiltersModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<AlertFiltersModalFormValues>
  ) => {
    setSubmitting(true);
    try {
      const expressions = values.expressions.flatMap((expression) => {
        if (expression.operation === undefined || expression.field === undefined || expression.target === undefined) {
          // filter out expressions that are for some reason not complete
          return [];
        }
        const formattedExpression = {
          ...expression,
          fieldType: expression.fieldType as InternalApiAlertFilterExpressionFieldTypeEnum,
          operation: expression.operation.key as InternalApiAlertFilterExpressionOperationEnum,
          target: expression.target.key as InternalApiAlertFilterExpressionTargetEnum,
          field: expression.field.key,
        };

        // For the STRINGLIST type format values
        if (expression.fieldType === InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST) {
          return {
            ...formattedExpression,
            value: JSON.stringify(expression.value.map((value: DropdownOption) => value.key)),
          };
        }

        // For the TIMESTAMP type format it
        if (expression.fieldType === InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
          if (expression.operation.key === 'IS_BETWEEN') {
            // At the moment "in between" is saved as two separate expressions
            return [
              {
                ...formattedExpression,
                operation: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
                value: expression.value[0].valueOf(),
              },
              {
                ...formattedExpression,
                operation: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
                value: expression.value[1].valueOf(),
              },
            ];
          }
          return {
            ...formattedExpression,
            value: expression.value.valueOf(), // This returns date in UNIX ms
          };
        }

        // For the NUMBER type format it in case of IS_BETWEEN
        if (expression.fieldType === InternalApiAlertFilterExpressionFieldTypeEnum.NUMBER) {
          if (expression.operation.key === 'IS_BETWEEN') {
            // At the moment "in between" is saved as two separate expressions
            return [
              {
                ...formattedExpression,
                operation: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
                value: Number(expression.value[0]),
              },
              {
                ...formattedExpression,
                operation: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
                value: Number(expression.value[1]),
              },
            ];
          }
          return {
            ...formattedExpression,
            value: Number(expression.value), // This returns number
          };
        }
        return formattedExpression;
      });

      if (values.id) {
        dispatch(
          updateAlertFilter(
            values.id,
            values.name,
            expressions,
            values.roles.map((role) => toInternalApiRole(role))
          )
        );
      } else {
        dispatch(
          addNewAlertFilter(
            values.name,
            expressions,
            values.roles.map((role) => toInternalApiRole(role))
          )
        );
      }
      setShowModal(false);
      resetForm(initialFormikState);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = (resetForm: any) => {
    setShowModal(false);
    resetForm({
      isSubmitting: false,
      isValidating: false,
      values: initialValues,
    });
  };

  const removeCondition = (expressions: any, index: number, setFieldValue: any) => {
    expressions.splice(index, 1);
    setFieldValue('expressions', expressions);
  };

  const updateCondition = (
    expressions: AlertFiltersConditionExpressionProps[],
    index: number,
    setFieldValue: any,
    newValue: AlertFiltersConditionExpressionProps
  ) => {
    expressions[index] = newValue;
    setFieldValue('expressions', expressions);
  };

  const setFilter = (setFieldValue: any) => {
    setShowModal(true);
    setFieldValue('name', selectedFilter?.name);
    setFieldValue('id', selectedFilter?.id);
    // Format back from database entry to objects used in FE
    const formattedExpressions = selectedFilter?.expressions.map((expression) => {
      const target = alertFiltersObjects.objects.find((object) => object.value.key === expression.target);
      const field = target?.fields.find((field) => field.value.key === expression.field);
      const operation = field?.operations.find((operation) => operation.key === expression.operation);
      let value: any = expression.value;
      if (expression.fieldType === InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST) {
        const values = JSON.parse(expression.value);
        value = findSelectedOptionValues(values, field?.options);
      } else if (expression.fieldType === InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
        value = dayjs(Number(expression.value));
      }
      return {
        ...expression,
        target: target?.value,
        field: field?.value,
        operation,
        value,
      };
    });
    setFieldValue('expressions', formattedExpressions);
  };
  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
      {(formikProps: FormikProps<AlertFiltersModalFormValues>) => {
        const { isSubmitting, setSubmitting, values, setFieldValue, setFieldTouched, resetForm, touched } = formikProps;
        return (
          <>
            <div className='buttons'>
              {selectedFilter && (
                <Button
                  variant='white'
                  size='large'
                  onClick={() => {
                    setFilter(setFieldValue);
                  }}
                >
                  Edit filter
                </Button>
              )}
              <Button
                variant='white'
                size='large'
                onClick={() => {
                  setShowModal(true);
                  setFieldValue('id', undefined);
                  setFieldValue('expressions', [alertFiltersObjects.createNewDefaultValues()]);
                }}
              >
                <FontAwesomeIcon className='fe me-2' icon={faPlus} aria-label='Add filter' />
                New filter
              </Button>
            </div>
            <SalvModal show={showModal} onHide={() => closeModal(resetForm)} className='alert-filters-modal'>
              <Form>
                <SalvModal.Header>Filter</SalvModal.Header>
                <SalvModal.Body>
                  <Row>
                    <Col md={12}>
                      <Input
                        name='name'
                        label='Name'
                        value={values.name}
                        inputSize='large'
                        onChange={(event: any) => {
                          setFieldValue('name', event.target.value);
                          setFieldTouched('name');
                        }}
                        isInvalid={touched.name && !values.name}
                      />
                    </Col>
                  </Row>
                  <Row key={values.expressions.length} className='mt-4'>
                    <Col md={12}>
                      <label>Filter conditions</label>
                      {values.expressions.map((expression, index) => (
                        <AlertFilterCondition
                          key={index}
                          expression={expression}
                          removable={index !== 0}
                          alertFiltersObjects={alertFiltersObjects}
                          onConditionChange={(change: any) =>
                            updateCondition(values.expressions, index, setFieldValue, change)
                          }
                          removeCondition={() => removeCondition(values.expressions, index, setFieldValue)}
                        />
                      ))}
                      <div className='mt-3'>
                        <Button
                          variant='secondary'
                          onClick={() => {
                            setFieldValue('expressions', [
                              ...values.expressions,
                              alertFiltersObjects.createNewDefaultValues(),
                            ]);
                          }}
                        >
                          <FontAwesomeIcon className='fe me-2' icon={faPlus} aria-label='Add condition' />
                          Add condition
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <AlertFilterRolesSelector
                    filterRoles={selectedFilter?.roles}
                    setFieldValue={setFieldValue}
                    selectedRoles={values.roles}
                  />
                </SalvModal.Body>
                <SalvModal.Footer>
                  {values.id && (
                    <Button
                      disabled={isSubmitting}
                      onClick={() => deleteFilter(values.id, setSubmitting, resetForm)}
                      size='large'
                      variant='outline-danger'
                    >
                      Delete
                    </Button>
                  )}
                  <Button variant='secondary' size='large' onClick={() => closeModal(resetForm)}>
                    Close
                  </Button>
                  {values.id && (
                    <Button
                      disabled={
                        isSubmitting ||
                        !values.expressions.every(
                          (expression) =>
                            expression.value?.length || (!Array.isArray(expression.value) && expression.value)
                        )
                      }
                      onClick={() => {
                        if (values.name) {
                          submit({ ...values, id: undefined }, formikProps);
                        }
                      }}
                      size='large'
                      variant='outline-primary'
                    >
                      Save as new
                    </Button>
                  )}
                  <Button
                    disabled={
                      isSubmitting ||
                      !values.expressions.every(
                        (expression) =>
                          expression.value?.length || (!Array.isArray(expression.value) && expression.value)
                      )
                    }
                    type='submit'
                    size='large'
                    variant='primary'
                  >
                    Save
                  </Button>
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </>
        );
      }}
    </Formik>
  );
};

interface Props {
  selectedFilter: InternalApiAlertFilter | undefined;
  alertFiltersObjects: AlertFilterObjects;
}

export default AlertFiltersModal;
